import { FunctionComponent, useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { Search } from 'react-feather'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'
import colors from '../../colors'
import { SPACING } from '../../constants'

interface Benefit {
  id: number
  uuid: string
  name: string
  unlimited: boolean
  quantity: number
  redeemedCount: number
  remaining: number | null
  fullyRedeemed: boolean
}

interface LookupResult {
  patron: {
    name: string
    email: string
  }
  membership: {
    id: number
    name: string
  }
  subscription: {
    id: number
    status: string
    endDate: string
    autoRenew: boolean
  }
  benefits: Benefit[]
  lookupCode: string
  organization?: {
    id: number
    name: string
  }
}

interface RecentLookup {
  code: string
  patronName?: string
}

const RECENT_LOOKUPS_KEY = 'walletLookup_recentCodes'
const MAX_RECENT_LOOKUPS = 3

const WalletLookup: FunctionComponent = () => {
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<LookupResult | null>(null)
  const [searched, setSearched] = useState(false)
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false)
  const [selectedBenefit, setSelectedBenefit] = useState<Benefit | null>(null)
  const [redeeming, setRedeeming] = useState(false)
  const [recentLookups, setRecentLookups] = useState<RecentLookup[]>([])
  const styles = useStyles()

  // Load recent lookups from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_LOOKUPS_KEY)
      if (stored) {
        setRecentLookups(JSON.parse(stored))
      }
    } catch {
      // Ignore localStorage errors
    }
  }, [])

  // Save recent lookup to localStorage
  const saveRecentLookup = (lookupCode: string, patronName?: string) => {
    setRecentLookups((prev) => {
      // Remove duplicate if exists
      const filtered = prev.filter((r) => r.code !== lookupCode)
      // Add to front
      const updated = [{ code: lookupCode, patronName }, ...filtered].slice(0, MAX_RECENT_LOOKUPS)
      // Persist
      try {
        localStorage.setItem(RECENT_LOOKUPS_KEY, JSON.stringify(updated))
      } catch {
        // Ignore localStorage errors
      }
      return updated
    })
  }

  const performLookup = async (lookupCode: string) => {
    if (!lookupCode.trim() || lookupCode.trim().length !== 4) {
      toast.error('Lookup code must be 4 characters')
      return
    }

    setLoading(true)
    setSearched(true)
    setResult(null)
    setCode('')

    try {
      const response = await axios.get(`/wallet/lookup/${lookupCode.toUpperCase()}`)
      setResult(response.data)
      saveRecentLookup(response.data.lookupCode, response.data.patron?.name)
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Result will be null, showing "not found" state
      } else {
        toast.error('An error occurred while searching. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    if (!code.trim()) {
      toast.error('Please enter a lookup code')
      return
    }
    performLookup(code)
  }

  const handleRecentClick = (recentCode: string) => {
    performLookup(recentCode)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return colors.GREEN
      case 'pending_cancel':
        return colors.YELLOW
      case 'expired':
      case 'canceled':
      case 'inactive':
        return colors.RED
      case 'payment_failed':
        return '#ff6b00' // Orange for payment issues
      default:
        return colors.GREY_3
    }
  }

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').toUpperCase()
  }

  // Check if subscription status allows benefit redemption
  const isSubscriptionActive = (status: string) => {
    return status === 'active' || status === 'pending_cancel'
  }

  // Get message for inactive subscription states
  const getInactiveMessage = (subscription: LookupResult['subscription']) => {
    const status = subscription.status
    if (status === 'expired' || status === 'inactive') {
      return `Membership expired ${subscription.endDate}. Patron can renew via VIP app.`
    }
    if (status === 'canceled') {
      return `Membership was canceled. Patron can re-subscribe via VIP app.`
    }
    if (status === 'payment_failed') {
      return `Payment failed. Patron needs to update payment method in VIP app.`
    }
    return null
  }

  const handleRedeemClick = (benefit: Benefit) => {
    setSelectedBenefit(benefit)
    setRedeemDialogOpen(true)
  }

  const handleRedeemCancel = () => {
    setRedeemDialogOpen(false)
    setSelectedBenefit(null)
  }

  const handleRedeemConfirm = async () => {
    if (!selectedBenefit || !result) return

    setRedeeming(true)
    try {
      await axios.post(`/patronage_benefits/${selectedBenefit.uuid}/redeem`)
      toast.success(`Successfully redeemed: ${selectedBenefit.name}`)
      setRedeemDialogOpen(false)
      setSelectedBenefit(null)
      // Re-fetch results to update benefit counts
      const response = await axios.get(`/wallet/lookup/${result.lookupCode}`)
      setResult(response.data)
    } catch (error: any) {
      if (error.response?.status === 422) {
        toast.error(error.response.data.error || 'Unable to redeem this benefit')
      } else {
        toast.error('An error occurred while redeeming. Please try again.')
      }
    } finally {
      setRedeeming(false)
    }
  }

  const canRedeem = (benefit: Benefit) => {
    // Can't redeem if subscription is inactive
    if (result && !isSubscriptionActive(result.subscription.status)) {
      return false
    }
    return !benefit.fullyRedeemed && (benefit.unlimited || (benefit.remaining !== null && benefit.remaining > 0))
  }

  const getProgressPercent = (benefit: Benefit) => {
    if (benefit.unlimited || benefit.quantity === 0) return 100
    const remaining = benefit.remaining ?? 0
    return (remaining / benefit.quantity) * 100
  }

  return (
    <Box className={styles.container}>
      {/* Search Section */}
      <Box className={styles.searchContainer}>
        <Typography variant="h4" gutterBottom>
          Wallet Lookup
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Enter a 4-character wallet code to look up a patron
        </Typography>

        <Box className={styles.searchRow}>
          <TextField
            label="Lookup Code"
            variant="outlined"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={handleKeyDown}
            inputProps={{
              maxLength: 4,
              style: {
                textTransform: 'uppercase',
                letterSpacing: '0.3em',
                fontSize: '2rem',
                textAlign: 'center',
                fontFamily: 'monospace'
              }
            }}
            className={styles.codeInput}
            placeholder="XXXX"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={loading || !code.trim()}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Search size={20} />}
            className={styles.searchButton}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </Box>

        {/* Recent Lookups */}
        {recentLookups.length > 0 && (
          <Box className={styles.recentContainer}>
            <Typography variant="caption" color="textSecondary">
              Recent:
            </Typography>
            <Box className={styles.recentChips}>
              {recentLookups.map((recent) => (
                <Chip
                  key={recent.code}
                  label={recent.code}
                  size="small"
                  onClick={() => handleRecentClick(recent.code)}
                  className={styles.recentChip}
                  title={recent.patronName || undefined}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Not Found State */}
      {searched && !loading && !result && (
        <Box className={styles.notFound}>
          <Typography variant="h6" color="textSecondary">
            No patron found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please check the code and try again
          </Typography>
        </Box>
      )}

      {/* Results */}
      {result && (
        <Box className={styles.resultContainer}>
          {/* Inactive Subscription Warning Banner */}
          {!isSubscriptionActive(result.subscription.status) && (
            <Box className={styles.inactiveBanner}>
              <Typography variant="body1" className={styles.inactiveBannerText}>
                {getInactiveMessage(result.subscription)}
              </Typography>
            </Box>
          )}

          {/* Benefits Cards */}
          <Box className={styles.benefitsHeader}>
            <Typography variant="h5">
              Benefits for <span className={styles.lookupCodeLarge}>{result.lookupCode}</span>
            </Typography>
            {isSubscriptionActive(result.subscription.status) ? (
              <Typography variant="body2" color="textSecondary">
                Click to redeem
              </Typography>
            ) : (
              <Typography variant="body2" className={styles.redemptionDisabledText}>
                Redemption disabled - membership not active
              </Typography>
            )}
          </Box>
          {result.benefits.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No active benefits at this time
            </Typography>
          ) : (
            <Box className={styles.benefitsGrid}>
              {result.benefits.map((benefit) => {
                const subscriptionInactive = !isSubscriptionActive(result.subscription.status)
                const cardClass = subscriptionInactive
                  ? styles.benefitDisabled
                  : benefit.fullyRedeemed
                    ? styles.benefitRedeemed
                    : styles.benefitClickable
                return (
                <Card
                  key={benefit.uuid}
                  className={`${styles.benefitCard} ${cardClass}`}
                  onClick={() => canRedeem(benefit) && handleRedeemClick(benefit)}
                >
                  <CardContent className={styles.benefitCardContent}>
                    <Typography variant="h6" className={styles.benefitName}>
                      {benefit.name}
                    </Typography>
                    {benefit.unlimited ? (
                      <Typography variant="h4" className={styles.benefitCount}>
                        Unlimited
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="h3" className={styles.benefitCount}>
                          {benefit.remaining}
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                          of {benefit.quantity} remaining
                        </Typography>
                        {/* Progress Bar */}
                        <Box className={styles.progressContainer}>
                          <LinearProgress
                            variant="determinate"
                            value={getProgressPercent(benefit)}
                            className={styles.progressBar}
                            classes={{
                              barColorPrimary: benefit.remaining === 0 ? styles.progressBarEmpty : styles.progressBarFilled
                            }}
                          />
                        </Box>
                      </>
                    )}
                    {benefit.fullyRedeemed && (
                      <Chip
                        label="FULLY REDEEMED"
                        size="small"
                        className={styles.redeemedChip}
                      />
                    )}
                  </CardContent>
                </Card>
              )
              })}
            </Box>
          )}

          {/* Patron Info Card */}
          <Card className={styles.card}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Patron Information
              </Typography>
              <Box className={styles.infoRow}>
                <Typography variant="body2" color="textSecondary">Name</Typography>
                <Typography variant="body1">{result.patron.name || '(Not provided)'}</Typography>
              </Box>
              <Box className={styles.infoRow}>
                <Typography variant="body2" color="textSecondary">Email</Typography>
                <Typography variant="body1">{result.patron.email}</Typography>
              </Box>
              {result.organization && (
                <Box className={styles.infoRow}>
                  <Typography variant="body2" color="textSecondary">Organization</Typography>
                  <Typography variant="body1">{result.organization.name}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Membership Info Card */}
          <Card className={styles.card}>
            <CardContent>
              <Box className={styles.membershipHeader}>
                <Typography variant="h6">
                  {result.membership.name}
                </Typography>
                <Chip
                  label={formatStatus(result.subscription.status)}
                  style={{
                    backgroundColor: getStatusColor(result.subscription.status),
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                  size="small"
                />
              </Box>
              <Box className={styles.infoRow}>
                <Typography variant="body2" color="textSecondary">Lookup Code</Typography>
                <Typography variant="body1" className={styles.lookupCode}>{result.lookupCode}</Typography>
              </Box>
              <Box className={styles.infoRow}>
                <Typography variant="body2" color="textSecondary">End Date</Typography>
                <Typography variant="body1">{result.subscription.endDate}</Typography>
              </Box>
              <Box className={styles.infoRow}>
                <Typography variant="body2" color="textSecondary">Auto-Renew</Typography>
                <Typography variant="body1">{result.subscription.autoRenew ? 'Yes' : 'No'}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Redeem Confirmation Dialog */}
      <Dialog
        open={redeemDialogOpen}
        onClose={handleRedeemCancel}
      >
        <DialogTitle>Confirm Redemption</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to redeem <strong>{selectedBenefit?.name}</strong>?
            {selectedBenefit && !selectedBenefit.unlimited && selectedBenefit.remaining !== null && (
              <> This patron has {selectedBenefit.remaining} remaining.</>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRedeemCancel} disabled={redeeming}>
            Cancel
          </Button>
          <Button
            onClick={handleRedeemConfirm}
            color="primary"
            variant="contained"
            disabled={redeeming}
          >
            {redeeming ? <CircularProgress size={20} color="inherit" /> : 'Confirm Redeem'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const useStyles = makeStyles({
  container: {
    padding: SPACING * 2,
    maxWidth: 900,
  },
  searchContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING * 2,
    marginTop: SPACING * 2,
    marginBottom: SPACING * 4,
    padding: SPACING * 3,
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING * 2,
  },
  codeInput: {
    width: 220,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
    },
  },
  searchButton: {
    height: 48,
    paddingLeft: SPACING * 4,
    paddingRight: SPACING * 4,
    minWidth: 160,
  },
  recentContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING,
    marginTop: SPACING,
  },
  recentChips: {
    display: 'flex',
    gap: SPACING,
  },
  recentChip: {
    fontFamily: 'monospace',
    fontWeight: 'bold',
    letterSpacing: '0.1em',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: colors.LIGHT_BLUE,
    },
  },
  notFound: {
    textAlign: 'center',
    padding: SPACING * 6,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
  },
  resultContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING * 2,
  },
  card: {
    marginBottom: SPACING * 2,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${SPACING}px 0`,
    borderBottom: '1px solid #eee',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  resultsFor: {
    marginBottom: SPACING * 2,
  },
  lookupCodeLarge: {
    fontFamily: 'monospace',
    letterSpacing: '0.2em',
    fontWeight: 'bold',
    color: colors.STITCH_BLUE,
  },
  lookupCode: {
    fontFamily: 'monospace',
    letterSpacing: '0.1em',
    fontWeight: 'bold',
  },
  membershipHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING * 2,
  },
  benefitsHeader: {
    marginBottom: SPACING * 2,
  },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: SPACING * 2,
    marginBottom: SPACING * 3,
  },
  benefitCard: {
    textAlign: 'center',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  benefitCardContent: {
    padding: SPACING * 3,
  },
  benefitRedeemed: {
    opacity: 0.6,
    backgroundColor: '#f5f5f5',
  },
  benefitClickable: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
  },
  benefitName: {
    fontWeight: 'bold',
    marginBottom: SPACING,
  },
  benefitCount: {
    color: colors.STITCH_BLUE,
    fontWeight: 'bold',
  },
  progressContainer: {
    marginTop: SPACING * 1.5,
    width: '100%',
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#e0e0e0',
  },
  progressBarFilled: {
    backgroundColor: colors.STITCH_BLUE,
  },
  progressBarEmpty: {
    backgroundColor: colors.RED,
  },
  redeemedChip: {
    marginTop: SPACING,
    backgroundColor: colors.RED,
    color: 'white',
  },
  inactiveBanner: {
    backgroundColor: '#fff3e0',
    border: '1px solid #ff9800',
    borderRadius: 8,
    padding: SPACING * 2,
    marginBottom: SPACING * 2,
  },
  inactiveBannerText: {
    color: '#e65100',
    fontWeight: 500,
  },
  redemptionDisabledText: {
    color: colors.RED,
    fontStyle: 'italic',
  },
  benefitDisabled: {
    opacity: 0.5,
    backgroundColor: '#f5f5f5',
    cursor: 'not-allowed',
    '&:hover': {
      transform: 'none',
      boxShadow: 'none',
    },
  },
})

export default WalletLookup
